// IP Geolocation utility
// Cache for IP geolocation data to avoid repeated API calls
const geolocationCache = {};

// Get geolocation data from IP address
export const getIPGeolocation = async (ip) => {
  // Skip if IP is invalid
  if (!ip || ip === 'Unknown' || ip === '127.0.0.1' || ip.startsWith('192.168.') || ip.startsWith('10.')) {
    return null;
  }

  // Check cache first
  if (geolocationCache[ip]) {
    return geolocationCache[ip];
  }

  try {
    // Using ip-api.com (free, no API key required, 45 requests/minute limit)
    // Note: For production, consider using a backend proxy to avoid CORS and rate limit issues
    const response = await fetch(`https://ip-api.com/json/${ip}?fields=status,message,country,countryCode,region,regionName,city,lat,lon,timezone,isp`, {
      method: 'GET',
      mode: 'cors',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.status === 'success') {
      const geolocation = {
        ip: ip,
        country: data.country,
        countryCode: data.countryCode,
        region: data.regionName,
        city: data.city,
        lat: data.lat,
        lon: data.lon,
        timezone: data.timezone,
        isp: data.isp
      };
      
      // Cache the result
      geolocationCache[ip] = geolocation;
      return geolocation;
    } else {
      console.warn(`Failed to get geolocation for IP ${ip}:`, data.message);
      return null;
    }
  } catch (error) {
    // Handle CORS or network errors gracefully
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.warn(`CORS or network error for IP ${ip}. Consider using a backend proxy.`);
    } else {
      console.error(`Error fetching geolocation for IP ${ip}:`, error);
    }
    return null;
  }
};

// Get geolocation for multiple IPs (with rate limiting)
export const getMultipleIPGeolocations = async (ips, delay = 200) => {
  const results = [];
  
  for (let i = 0; i < ips.length; i++) {
    const ip = ips[i];
    const geolocation = await getIPGeolocation(ip);
    
    if (geolocation) {
      results.push(geolocation);
    }
    
    // Add delay to respect API rate limits (45 requests/minute = ~1.3 seconds between requests)
    if (i < ips.length - 1) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  return results;
};

// Clear geolocation cache
export const clearGeolocationCache = () => {
  Object.keys(geolocationCache).forEach(key => delete geolocationCache[key]);
};

